import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  getContinuityOnboardingStep,
  setContinuityOnboardingStep,
  type IOnboardingStep,
} from 'src/utils/continuityOnboardingLocalStorage';
import { EscrowPartyTypeEnum, AgreementFormEnum } from 'src/graphql/schema';
import { ContinuityEscrowOnboardingRoutesEnum } from 'src/pages/ContinuityEscrowOnboarding/model/routes.enum';

import { createRequiredContext } from '../createRequiredContext';

import { useContinuityEscrow } from './ContinuityEscrowContext';

const [useContinuityEscrowOnboarding, ContinuityEscrowOnboardingProvider] = createRequiredContext<{
  handleNextStep: () => void;
  handlePrevStep: () => void;
  totalSteps: number;
  currentStep: number;
  lastCompletedStep: number;
  showBackButton: boolean;
}>();

// Define the possible step sequences as constants for clarity
const CREATOR_DEPOSITOR_BIPARTITE_FULL_FLOW = [
  'depositor',
  'beneficiary',
  'notification-settings',
  'activation-notifications',
  'agreement-setup',
];
const CREATOR_TRIPARTITE_INITIAL_FLOW = {
  [EscrowPartyTypeEnum.Depositor]: [
    'depositor',
    'beneficiary',
    'notification-settings',
    'environment-visibility',
    'agreement-setup',
  ],
  [EscrowPartyTypeEnum.Beneficiary]: ['depositor', 'beneficiary', 'environment-visibility', 'agreement-setup'],
};

const createCreatorFlow = ({
  party,
  agreementForm,
  isThirdPartyAgreement,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isThirdPartyAgreement: boolean;
}): IOnboardingStep[] => {
  const baseFlow =
    agreementForm === AgreementFormEnum.Bipartite
      ? CREATOR_DEPOSITOR_BIPARTITE_FULL_FLOW
      : CREATOR_TRIPARTITE_INITIAL_FLOW[party];
  const flowSteps = [...baseFlow, ...(isThirdPartyAgreement ? [] : ['agreement-details']), 'summary'];

  return flowSteps as IOnboardingStep[];
};

const createInvitedFlow = (beneficiarySharedInformation: boolean): IOnboardingStep[] => [
  'depositor',
  'beneficiary',
  ...(beneficiarySharedInformation ? (['notification-settings'] as const) : []),
  'summary',
];

function createSteps({
  party,
  agreementForm,
  isInvitedParty,
  isThirdPartyAgreement,
  beneficiarySharedInformation,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isInvitedParty: boolean;
  isThirdPartyAgreement: boolean;
  beneficiarySharedInformation: boolean;
}): IOnboardingStep[] {
  const flowSteps = isInvitedParty
    ? createInvitedFlow(beneficiarySharedInformation)
    : createCreatorFlow({ party, agreementForm, isThirdPartyAgreement });

  // Only the beneficiary manages members during onboarding
  return party === EscrowPartyTypeEnum.Beneficiary ? ['members', ...flowSteps] : flowSteps;
}

// Define a linked list node structure
type IOnboardingStepNode = {
  step: IOnboardingStep;
  prev: IOnboardingStepNode | null;
  next: IOnboardingStepNode | null;
};

function buildLinkedList({
  party,
  agreementForm,
  isInvitedParty,
  isThirdPartyAgreement,
  beneficiarySharedInformation,
}: {
  party: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  isInvitedParty: boolean;
  isThirdPartyAgreement: boolean;
  beneficiarySharedInformation: boolean;
}): IOnboardingStepNode[] {
  const steps = createSteps({
    party,
    agreementForm,
    isInvitedParty,
    isThirdPartyAgreement,
    beneficiarySharedInformation,
  });
  const nodes: IOnboardingStepNode[] = [];

  steps.forEach((step, i) => {
    const node = {
      step,
      prev: i > 0 ? nodes[i - 1] : null,
      next: null,
    };

    if (i > 0) {
      nodes[i - 1].next = node;
    }

    nodes.push(node);
  });

  return nodes;
}

const ContinuityEscrowOnboardingContextProvider = ({ children }: React.PropsWithChildren) => {
  const {
    continuityEscrow: { id, currentCompanyRole, startedBy, agreement, beneficiarySharedInformation },
  } = useContinuityEscrow();
  const navigate = useNavigate();

  const isInvitedParty = currentCompanyRole !== startedBy;
  const stepLinkedList = buildLinkedList({
    party: currentCompanyRole!,
    agreementForm: agreement.agreementForm!,
    isInvitedParty,
    isThirdPartyAgreement: Boolean(agreement?.thirdPartyAgreement),
    beneficiarySharedInformation,
  });

  // Create a map for easy lookup by step name
  const stepLinkedListMap = stepLinkedList.reduce(
    (map, node) => {
      map[node.step] = node;
      return map;
    },
    {} as Record<IOnboardingStep, IOnboardingStepNode>,
  );

  const { '*': slug } = useParams<{ '*': ContinuityEscrowOnboardingRoutesEnum }>();

  const firstStep = stepLinkedList[0].step;
  const storedStep = getContinuityOnboardingStep(id);
  const lastCompletedStep = storedStep && storedStep in stepLinkedListMap ? storedStep : firstStep;
  const currentStep = slug && slug in stepLinkedListMap ? slug : firstStep;

  // Resume onboarding at the last completed step on entry
  useEffect(() => {
    navigate(`/continuity-escrows/onboarding/${id}/${lastCompletedStep}`, { replace: true });
  }, [id]);

  const currentStepIndex = stepLinkedList.findIndex((step) => step.step === currentStep) + 1;
  const lastCompletedStepIndex = stepLinkedList.findIndex((step) => step.step === lastCompletedStep) + 1;

  const handleNextStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const nextNode = currentNode.next;

    // Already at last step
    if (!nextNode) return;

    // Store the next step in the local storage
    if (currentStepIndex >= lastCompletedStepIndex) setContinuityOnboardingStep(id, nextNode.step);

    // Build URL for next step
    const url = `/continuity-escrows/onboarding/${id}/${nextNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate, lastCompletedStepIndex, currentStepIndex]);

  const handlePrevStep = useCallback(() => {
    const currentNode = stepLinkedListMap[currentStep];

    if (!currentNode) return;

    const prevNode = currentNode.prev;

    // Already at first step
    if (!prevNode) return;

    // Build URL for previous step
    const url = `/continuity-escrows/onboarding/${id}/${prevNode.step}`;

    navigate(url);
  }, [currentStep, id, stepLinkedListMap, navigate]);

  const providerValue = {
    handleNextStep,
    handlePrevStep,
    ...calculateStepMetrics({
      isInvitedParty,
      baseStepCount: stepLinkedList.length,
      currentIndex: currentStepIndex,
      lastCompletedIndex: lastCompletedStepIndex,
    }),
  };

  return <ContinuityEscrowOnboardingProvider value={providerValue}>{children}</ContinuityEscrowOnboardingProvider>;
};

function calculateStepMetrics({
  isInvitedParty,
  baseStepCount,
  currentIndex,
  lastCompletedIndex,
}: {
  isInvitedParty: boolean;
  baseStepCount: number;
  currentIndex: number;
  lastCompletedIndex: number;
}) {
  if (isInvitedParty)
    return {
      totalSteps: baseStepCount,
      currentStep: currentIndex,
      lastCompletedStep: lastCompletedIndex - 1,
      showBackButton: currentIndex > 1,
    };

  return {
    totalSteps: baseStepCount + 1,
    currentStep: currentIndex + 1,
    lastCompletedStep: lastCompletedIndex,
    showBackButton: currentIndex > 1,
  };
}

export { useContinuityEscrowOnboarding, ContinuityEscrowOnboardingContextProvider };
